import { parseDbTimestamp } from "@repo/shared";
import type {
  ComplianceCheckStatus,
  ComplianceFlowRow,
  ComplianceReviewAction,
  ComplianceReviewLogEntry,
  Translated,
} from "@repo/shared/types";
import { Check, ChevronRight, Flag, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./check-dialog.i18n";

/** The three states, in order, so the stepper and its progress read off one list. */
const STEPS: Array<{
  status: ComplianceCheckStatus;
  icon: typeof Flag;
  key: "stepNotReviewed" | "stepFlagged" | "stepApproved";
}> = [
  { status: "not_reviewed", icon: ShieldCheck, key: "stepNotReviewed" },
  { status: "flagged", icon: Flag, key: "stepFlagged" },
  { status: "approved", icon: Check, key: "stepApproved" },
];

/** Same tone as the Review Status column, so an action reads alike in both places. */
const ACTION_VARIANT: Record<ComplianceReviewAction, "success" | "warning" | "destructive"> = {
  flag: "warning",
  remark: "destructive",
  approve: "success",
};

/**
 * The compliance review of one COMPLETED flow, as a process rather than a form.
 *
 * Deliberately NOT the approval dialog: nothing here moves the flow through its
 * own process. What it does enforce is the review's own shape —
 *
 * - a flow is flagged from the LIST, never from in here;
 * - while flagged, a reviewer may log as many remarks as the review needs;
 * - approving is the only exit, and it closes the dialog to further action.
 *
 * So the available actions come from the standing, not from a status picker: a
 * reviewer cannot drop an approved review back into the phase, or conclude one
 * that was never opened.
 */
export function CheckDialog({
  row,
  open,
  onClose,
  onSubmit,
  saving,
  error,
  log,
  logLoading,
  recorded,
  formatDateTime,
}: {
  row: ComplianceFlowRow | null;
  open: boolean;
  onClose: () => void;
  onSubmit: (action: ComplianceReviewAction, notes: string) => void;
  saving: boolean;
  error: string | null;
  /** The flow's review history, newest first. */
  log: ComplianceReviewLogEntry[];
  logLoading: boolean;
  /** Increments on each recorded action — the signal to empty the note box. */
  recorded: number;
  /** How a date is rendered, so the dialog follows the tenant's regional format. */
  formatDateTime: (value: Date) => string;
}) {
  const t = useT(T);
  const [notes, setNotes] = useState("");

  // The note is never seeded: it lives in the history above, so pre-filling it
  // would show the same text twice and invite saving an old reason as a new one.
  // Cleared again after each recorded action, since a review can log several
  // remarks without the dialog closing in between.
  useEffect(() => {
    setNotes("");
  }, [open, recorded]);

  if (!row) return null;

  const status = row.complianceCheck.status;
  const currentStep = STEPS.findIndex((s) => s.status === status);
  const canAct = status === "flagged";

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* Wider than the default `max-w-lg`: the three process steps plus their
          arrows need one unbroken line, and the Norwegian labels are longer
          than the English. A wrapped stepper leaves a dangling arrow and stops
          reading as a sequence. */}
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>
            {t.description(row.flowInstanceName || `${row.flowName} — ${row.clientName}`)}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Where this review stands, and which way it runs. The arrows carry the
              direction: a thin connector line read as three unrelated badges,
              especially once "Flagged for review" wrapped onto two lines. Labels
              never wrap now, and a step that is behind us shows a tick rather
              than its own icon, so past and future are told apart at a glance. */}
          <ol className="flex flex-wrap items-center gap-y-2 rounded-lg border bg-muted/30 px-2 py-2">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const done = index < currentStep;
              const active = index === currentStep;
              return (
                <li key={step.status} className="flex items-center">
                  <span
                    className={cn(
                      "flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-2",
                      active && "bg-primary/10",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                        done && "border-primary/40 bg-primary/20 text-primary",
                        active && "border-primary bg-primary text-primary-foreground",
                        !done && !active && "border-border text-muted-foreground/60",
                      )}
                    >
                      {done ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                    </span>
                    <span
                      className={cn(
                        "whitespace-nowrap text-sm",
                        active && "font-medium",
                        done && "text-muted-foreground",
                        !done && !active && "text-muted-foreground/60",
                      )}
                    >
                      {t[step.key]}
                    </span>
                  </span>
                  {index < STEPS.length - 1 && (
                    <ChevronRight
                      aria-hidden
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50"
                    />
                  )}
                </li>
              );
            })}
          </ol>

          <div className="space-y-2">
            <Label>{t.logTitle}</Label>
            {logLoading ? (
              <p className="text-sm text-muted-foreground">…</p>
            ) : log.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t.logEmpty}</p>
            ) : (
              <ul className="space-y-3 max-h-56 overflow-y-auto pr-1">
                {log.map((entry) => {
                  // `at` is a bare Postgres timestamp; read as-is it lands in
                  // local time, so the helper pins it to the UTC it really is.
                  const at = parseDbTimestamp(entry.at);
                  return (
                    <li key={entry.id} className="text-sm">
                      <span className="inline-flex items-center gap-2">
                        <Badge variant={ACTION_VARIANT[entry.action]}>
                          {actionLabel(entry.action, t)}
                        </Badge>
                        <span className="text-muted-foreground text-xs">
                          {t.logEntry(entry.actorName ?? "—", at ? formatDateTime(at) : "—")}
                        </span>
                      </span>
                      {entry.notes && <p className="mt-1 text-muted-foreground">{entry.notes}</p>}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {canAct ? (
            <div className="space-y-2">
              <Label htmlFor="check-notes">{t.notes}</Label>
              <Textarea
                id="check-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t.notesPlaceholder}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">{t.notesRequired}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {status === "approved" ? t.hintApproved : t.hintNotReviewed}
            </p>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t.close}
          </Button>
          {canAct && (
            <>
              <Button
                variant="outline"
                onClick={() => onSubmit("remark", notes)}
                disabled={!notes.trim()}
                loading={saving}
              >
                {t.addRemark}
              </Button>
              <Button
                onClick={() => onSubmit("approve", notes)}
                disabled={!notes.trim()}
                loading={saving}
              >
                {t.approve}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function actionLabel(action: ComplianceReviewAction, t: Translated<typeof T>): string {
  if (action === "flag") return t.actionFlag;
  if (action === "remark") return t.actionRemark;
  return t.actionApprove;
}
